import React from 'react';
import { connect } from 'react-redux';
import { Field, formValueSelector, InjectedFormProps, reduxForm } from 'redux-form';
import { Button, Card, CardBody, Col, FormGroup } from 'reactstrap';
import ReduxFormInput from '@Utils/ReduxFormInput';
import validate, { ArmyListCreateParams } from './validate';
import ReduxFormSelect from '@Utils/ReduxFormSelect';
import { FolderView } from '@Selectors/builder/myBuilder/builder/new/view/FolderView';
import { AppState } from '@Redux/store';
import ReduxFormCheck from '@Utils/ReduxFormCheck';
import ReduxFormCategorySelect from '@Utils/ReduxFormCategorySelect';
import { VersionGameView } from '@Selectors/common/view/VersionGameView';
import { ArmyGameView } from '@Selectors/common/view/ArmyGameView';
import { getUserPreference } from '@Selectors/profile/selector/getUserPreference';
import { FormattedMessage } from 'react-intl';
import { defaultMessages } from '@I18n/default';

interface Props {
  versions: Array<VersionGameView>,
  armies: Array<ArmyGameView>,
  folders: Array<FolderView>,
  cancel: () => void,
  importList: () => void,
  onSubmit: (values: any) => void,
  canSubmit: boolean,
}

const Form: React.FC<Props & InjectedFormProps<ArmyListCreateParams, Props>> = (
  {
    versions,
    armies,
    folders,
    importList,
    cancel,
    handleSubmit,
    canSubmit
  }
) => {
  return (
    <form onSubmit={handleSubmit}>
      <Col sm="12">
        <Card className="card-border">
          <CardBody>
            <h2 className="text-center"><FormattedMessage {...defaultMessages.armyListNewTitle} /></h2>

            <p><FormattedMessage {...defaultMessages.armyListNewInfos} /></p>
            <FormGroup row={true}>
              <Col xs="12" md={6} lg="4">
                <Field
                  name="versionId"
                  type="text"
                  datas={versions}
                  component={ReduxFormCategorySelect}
                  label={defaultMessages.armyListFormVersionTitle}
                  placeHolder={defaultMessages.armyListFormVersionPlaceholder}
                />
              </Col>
              <Col xs="12" md={6} lg="4">
                <Field
                  name="armyId"
                  type="text"
                  datas={armies}
                  component={ReduxFormCategorySelect}
                  label={defaultMessages.armyListFormArmyTitle}
                  placeHolder={defaultMessages.armyListFormArmyPlaceholder}
                />
              </Col>
              <Col xs="12" md={12} lg="4">
                <Field
                  name="name"
                  type="text"
                  component={ReduxFormInput}
                  label={defaultMessages.armyListFormNameTitle}
                  placeHolder={defaultMessages.armyListFormNamePlaceholder}
                />
              </Col>
            </FormGroup>

            <FormGroup row={true}>
              <Col xs="12" lg="4">
                <Field
                  name="max"
                  type="number"
                  component={ReduxFormInput}
                  label={defaultMessages.armyListFormMaxTitle}
                  placeHolder={defaultMessages.armyListFormMaxPlaceholder}
                />
              </Col>
            </FormGroup>

            <FormGroup row={true}>
              <Col xs="12" md={6} lg={4}>
                <Field
                  name="folderId"
                  type="text"
                  datas={folders}
                  component={ReduxFormSelect}
                  label={defaultMessages.armyListFormFolderTitle}
                  placeHolder={defaultMessages.armyListFormFolderPlaceholder}
                />
              </Col>
              <Col xs="12" md={6} lg="4">
                <Field
                  name="isPrivate"
                  type="checkbox"
                  component={ReduxFormCheck}
                  label={defaultMessages.armyListFormPrivateTitle}
                />
              </Col>
            </FormGroup>

            <FormGroup row={true}>
              <Col xs="12" lg="12">
                <Field
                  name="notes"
                  type="textarea"
                  component={ReduxFormInput}
                  label={defaultMessages.armyListFormNotesTitle}
                  placeHolder={defaultMessages.armyListFormNotesPlaceholder}
                />
              </Col>
            </FormGroup>

          </CardBody>
          <div className="mb-2 text-center">
            <Button disabled={!canSubmit} onClick={() => cancel()} color="age"
                    type="button"><FormattedMessage {...defaultMessages.formCancel} /></Button>
            <Button disabled={!canSubmit} color="age" type="submit"
                    className="ms-1"><FormattedMessage {...defaultMessages.formCreate} /></Button>
            <Button onClick={() => importList()} color="age"
                    className="ms-1"><FormattedMessage {...defaultMessages.formImport} /></Button>
          </div>
        </Card>
      </Col>
    </form>
  );
};

const NewArmyListForm = reduxForm<ArmyListCreateParams, Props>({
  form: 'new_army_list',
  validate
})(Form);

// Decorate with connect to read form values
// @ts-ignore
const selector = formValueSelector('new_army_list'); // <-- same as form name

const mapStateToProps = (state: AppState, props: Props) => {
  // can select values individually
  let version = selector(state, 'versionId') ?? null;
  const userPreference = getUserPreference(state);

  if (!version) {
    version = props.versions
      .flatMap(item => item.options)
      .filter(item => item.game === userPreference.game)
      .sort((a, b) => b.id - a.id)
      .slice(0, 1)
      .shift()
      ?.id;
  }

  const armies = props.armies.filter(item => version != null && item.versionId == version);

  return {
    versions: props.versions,
    armies: armies.sort((a, b) => a.source - b.source),
    folders: props.folders,
    onSubmit: props.onSubmit,
    initialValues: {
      max: 4500,
      versionId: version,
      armyId: armies.slice(0, 1)
        .shift()
        ?.options
        ?.slice(0, 1)
        ?.shift()
        ?.id
    }
  };
};

export default connect(mapStateToProps)(NewArmyListForm);
